import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import SpeechInputButton from "../components/SpeechInputButton";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";
import AiModuleTabs from "../components/AiModuleTabs";

export default function AiTwinPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [twin, setTwin] = useState(null);
  const [profileBrief, setProfileBrief] = useState(null);
  const bottomRef = useRef(null);

  useSeo({
    title: "和另一个自己对话 | AI数字分身",
    metaDescription: "基于你的人格模型与长期记忆，和懂你的 AI 分身对话。",
    path: "/ai/twin",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/twin")}`, {
        replace: true,
      });
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { res, data } = await apiJson(`/api/ai/twin/session?user_id=${userId}`);
      if (!res.ok) throw new Error(data?.detail || "加载失败");
      setMessages(data.messages || []);
      setHasProfile(Boolean(data.has_profile));
      setTwin(data.twin || null);
      setProfileBrief(data.profile || null);
    } catch (err) {
      alert(err.message || "加载数字分身失败");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !userId) return;
    setSending(true);
    setInput("");
    setMessages((prev) => [
      ...prev,
      { id: `tmp-${Date.now()}`, role: "user", content: text },
    ]);
    try {
      const { res, data } = await apiJson("/api/ai/twin/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, message: text }),
      });
      if (!res.ok) throw new Error(data?.detail || "发送失败");
      // 保留乐观用户气泡，仅替换 tmp id，再追加助手回复（避免重复插入用户消息）
      setMessages((prev) => {
        const next = prev.map((m) =>
          String(m.id).startsWith("tmp-")
            ? { id: `u-${Date.now()}`, role: "user", content: text }
            : m
        );
        return [...next, data.message];
      });
      if (data.twin) setTwin(data.twin);
      if (typeof data.has_profile === "boolean") setHasProfile(data.has_profile);
      if (data.profile) setProfileBrief(data.profile);
    } catch (err) {
      alert(err.message || "发送失败");
      setMessages((prev) => prev.filter((m) => !String(m.id).startsWith("tmp-")));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("确定清空与数字分身的对话？人格档案不会删除。")) return;
    try {
      const { res, data } = await apiJson("/api/ai/twin/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) throw new Error(data?.detail || "重置失败");
      setMessages(data.messages || []);
    } catch (err) {
      alert(err.message || "重置失败");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-5">
          <AiModuleTabs groupKey="persona" />
        </div>
        <section className="lg:col-span-3 flex flex-col min-h-[70vh] rounded-2xl bg-white border border-rose-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-rose-50 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="text-lg font-bold text-gray-900">和另一个自己对话</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {twin?.agent_name || "AI 数字分身"} · 基于人格模型与长期记忆
              </p>
            </div>
            <div className="flex gap-2 text-xs">
              <Link to="/ai/personality" className="text-rose-600 hover:underline">
                人格档案
              </Link>
              <button
                type="button"
                onClick={handleReset}
                className="text-gray-400 hover:text-rose-500"
              >
                清空对话
              </button>
            </div>
          </div>

          {!hasProfile && !loading && (
            <div className="mx-4 mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800">
              你还没有完整人格档案，分身回答会偏通用。建议先
              <Link to="/ai/consultant" className="underline mx-1">
                完成 AI 咨询师访谈
              </Link>
              或
              <Link to="/ai/personality" className="underline mx-1">
                查看人格页
              </Link>
              。
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {loading ? (
              <p className="text-center text-gray-400 text-sm py-10">加载中…</p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-rose-500 text-white"
                        : "bg-rose-50 text-gray-800 border border-rose-100"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="text-xs text-gray-400 pl-1">另一个自己正在思考…</div>
            )}
            <div ref={bottomRef} />
          </div>

          <form
            onSubmit={sendMessage}
            className="border-t border-rose-50 p-3 flex items-end gap-2"
          >
            <SpeechInputButton
              disabled={sending || loading}
              maxLength={2000}
              getPrefix={() => input}
              onResult={(text) => {
                if (!text) return;
                setInput(text);
              }}
            />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={1}
              maxLength={2000}
              placeholder="例如：我适合找什么样的人？"
              className="flex-1 border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 resize-y min-h-[48px] max-h-40"
              disabled={sending || loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={sending || loading || !input.trim()}
              className="px-5 py-3 rounded-full bg-rose-500 text-white text-sm font-medium disabled:opacity-50 hover:bg-rose-600"
            >
              发送
            </button>
          </form>
        </section>

        <aside className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl bg-white border border-rose-100 p-5 shadow-sm space-y-3">
            <h3 className="font-bold text-gray-900">分身依据</h3>
            {profileBrief ? (
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  <span className="text-gray-400">性格：</span>
                  {profileBrief.personality_type || profileBrief.personality || "—"}
                </p>
                <p>
                  <span className="text-gray-400">沟通：</span>
                  {profileBrief.communication_style || "—"}
                </p>
                <p>
                  <span className="text-gray-400">关系模式：</span>
                  {profileBrief.relationship_style || "—"}
                </p>
                <p>
                  <span className="text-gray-400">核心价值：</span>
                  {profileBrief.value_system || "—"}
                </p>
                <p>
                  <span className="text-gray-400">成长方向：</span>
                  {profileBrief.growth_direction || "—"}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">完成访谈后，这里会展示你的人格摘要。</p>
            )}
            <Link
              to="/ai/personality"
              className="inline-block text-sm text-rose-600 underline"
            >
              查看完整人格雷达 →
            </Link>
          </div>

          <div className="rounded-2xl bg-white border border-gray-100 p-5 text-sm text-gray-600 space-y-2">
            <h4 className="font-semibold text-gray-800">可以这样问</h4>
            <ul className="space-y-1.5 text-xs">
              <li>· 我适合找什么样的人？</li>
              <li>· 我在关系里容易踩哪些坑？</li>
              <li>· 我现在最该提升什么？</li>
              <li>· 如果对方不回消息，我通常会怎么反应？</li>
            </ul>
            <Link to="/center/ai-memory" className="inline-block text-xs text-rose-600 underline pt-1">
              管理长期记忆
            </Link>
          </div>
        </aside>
      </main>
      <Footer />
    </div>
  );
}
